/** Browser WebAuthn helpers for passkey registration and login. */

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64urlToBuffer(value: string): ArrayBuffer {
  const padded = value + '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function decodeCreationOptions(options: Record<string, unknown>): CredentialCreationOptions {
  const publicKey = options.publicKey as Record<string, unknown>
  return {
    publicKey: {
      ...(publicKey as unknown as PublicKeyCredentialCreationOptions),
      challenge: base64urlToBuffer(String(publicKey.challenge)),
      user: {
        ...(publicKey.user as PublicKeyCredentialUserEntity),
        id: base64urlToBuffer(String((publicKey.user as Record<string, unknown>).id)),
      },
      excludeCredentials: ((publicKey.excludeCredentials as Array<Record<string, unknown>>) || []).map((cred) => ({
        ...cred,
        id: base64urlToBuffer(String(cred.id)),
      })) as PublicKeyCredentialDescriptor[],
    },
  }
}

function decodeRequestOptions(options: Record<string, unknown>): CredentialRequestOptions {
  const publicKey = options.publicKey as Record<string, unknown>
  return {
    publicKey: {
      ...(publicKey as unknown as PublicKeyCredentialRequestOptions),
      challenge: base64urlToBuffer(String(publicKey.challenge)),
      allowCredentials: ((publicKey.allowCredentials as Array<Record<string, unknown>>) || []).map((cred) => ({
        ...cred,
        id: base64urlToBuffer(String(cred.id)),
      })) as PublicKeyCredentialDescriptor[],
    },
  }
}

function serializeCredential(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse | AuthenticatorAssertionResponse
  const payload: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
  }

  if ('attestationObject' in response) {
    payload.response = {
      ...(payload.response as Record<string, unknown>),
      attestationObject: bufferToBase64url(response.attestationObject),
    }
  }
  if ('authenticatorData' in response) {
    payload.response = {
      ...(payload.response as Record<string, unknown>),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    }
  }
  return payload
}

export async function registerPasskey(options: Record<string, unknown>, deviceName?: string) {
  if (!window.PublicKeyCredential) {
    throw new Error('Passkeys are not supported in this browser')
  }
  const creationOptions = decodeCreationOptions(options)
  const credential = (await navigator.credentials.create(creationOptions)) as PublicKeyCredential | null
  if (!credential) throw new Error('Passkey registration was cancelled')
  return { credential: serializeCredential(credential), device_name: deviceName }
}

export async function loginWithPasskey(email: string, options: Record<string, unknown>) {
  if (!window.PublicKeyCredential) {
    throw new Error('Passkeys are not supported in this browser')
  }
  const requestOptions = decodeRequestOptions(options)
  const credential = (await navigator.credentials.get(requestOptions)) as PublicKeyCredential | null
  if (!credential) throw new Error('Passkey sign-in was cancelled')
  return { email, credential: serializeCredential(credential) }
}

export function passkeysSupported() {
  return typeof window !== 'undefined' && Boolean(window.PublicKeyCredential)
}
